import { useEffect, useState, type FormEvent } from "react";
import {
  createExternalService,
  deleteExternalService,
  fetchExternalServices,
  issueExternalServiceToken,
  revokeExternalServiceToken,
  updateExternalService,
} from "@/api/externalServices";
import { ApiError } from "@/api/client";
import {
  AdminShell,
  FieldLabel,
  TextArea,
  TextInput,
} from "@/components/admin/AdminShell";
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconKey,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconX,
} from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthProvider";
import type { ExternalServiceDto } from "@/types/externalService";

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function formatDateTime(value: string | null): string {
  if (!value) return "Никогда";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SecretDialog({
  title,
  token,
  onClose,
}: {
  title: string;
  token: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setCopyError(null);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopyError("Не удалось скопировать токен");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/35 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="service-token-dialog-title"
    >
      <div className="w-full max-w-2xl rounded-lg border border-slate-200 bg-white shadow-2xl">
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2
              id="service-token-dialog-title"
              className="font-onest text-base font-semibold text-slate-950"
            >
              {title}
            </h2>
            <p className="mt-0.5 font-onest text-xs text-amber-700">
              Сохраните значение сейчас. Повторно оно не отображается.
            </p>
          </div>
          <button
            type="button"
            title="Закрыть"
            aria-label="Закрыть"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            onClick={onClose}
          >
            <IconX size={18} />
          </button>
        </header>
        <div className="p-5">
          <div className="flex items-stretch gap-2">
            <code className="min-w-0 flex-1 break-all rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 font-mono text-sm text-slate-800">
              {token}
            </code>
            <button
              type="button"
              title="Скопировать токен"
              aria-label="Скопировать токен"
              className="inline-flex w-11 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              onClick={() => void copy()}
            >
              {copied ? <IconCheck size={17} /> : <IconCopy size={17} />}
            </button>
          </div>
          {copyError ? (
            <p className="mt-2 font-onest text-xs text-red-600">{copyError}</p>
          ) : null}
          <div className="mt-4 rounded-md bg-slate-50 px-3 py-2 font-mono text-xs text-slate-600">
            Authorization: Bearer {"{token}"}
          </div>
          <div className="mt-4 flex justify-end">
            <Button className="px-4 py-2 text-sm" onClick={onClose}>
              Готово
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExternalServicesPage() {
  const { roles } = useAuth();
  const isAdmin = roles.includes("admin");
  const [services, setServices] = useState<ExternalServiceDto[]>([]);
  const [loading, setLoading] = useState(isAdmin);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tokenName, setTokenName] = useState("production");
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingId, setSavingId] = useState<number | null>(null);
  const [issuingId, setIssuingId] = useState<number | null>(null);
  const [issueNames, setIssueNames] = useState<Record<number, string>>({});
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [secret, setSecret] = useState<{ title: string; token: string } | null>(
    null,
  );

  const reload = async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      setServices(await fetchExternalServices());
    } catch (reason) {
      setError(errorMessage(reason, "Не удалось загрузить внешние сервисы"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Внешние сервисы";
    void reload();
  }, [isAdmin]);

  const replaceService = (service: ExternalServiceDto) => {
    setServices((current) =>
      current
        .map((item) => (item.id === service.id ? service : item))
        .sort((a, b) => a.name.localeCompare(b.name, "ru")),
    );
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !tokenName.trim()) return;

    setCreating(true);
    setActionError(null);
    try {
      const created = await createExternalService({
        name: name.trim(),
        description: description.trim() || null,
        tokenName: tokenName.trim(),
      });
      setServices((current) =>
        [...current, created.service].sort((a, b) =>
          a.name.localeCompare(b.name, "ru"),
        ),
      );
      setExpanded((current) => new Set(current).add(created.service.id));
      setName("");
      setDescription("");
      setTokenName("production");
      setSecret({
        title: `Токен сервиса «${created.service.name}»`,
        token: created.token,
      });
    } catch (reason) {
      setActionError(errorMessage(reason, "Не удалось создать сервис"));
    } finally {
      setCreating(false);
    }
  };

  const beginEdit = (service: ExternalServiceDto) => {
    setEditingId(service.id);
    setEditName(service.name);
    setEditDescription(service.description ?? "");
    setActionError(null);
  };

  const saveEdit = async (serviceId: number) => {
    if (!editName.trim()) return;
    setSavingId(serviceId);
    setActionError(null);
    try {
      const updated = await updateExternalService(serviceId, {
        name: editName.trim(),
        description: editDescription.trim() || null,
      });
      replaceService(updated);
      setEditingId(null);
    } catch (reason) {
      setActionError(errorMessage(reason, "Не удалось обновить сервис"));
    } finally {
      setSavingId(null);
    }
  };

  const removeService = async (service: ExternalServiceDto) => {
    if (
      !window.confirm(`Удалить внешний сервис «${service.name}» и его токены?`)
    ) {
      return;
    }

    setActionError(null);
    try {
      await deleteExternalService(service.id);
      setServices((current) =>
        current.filter((item) => item.id !== service.id),
      );
    } catch (reason) {
      setActionError(errorMessage(reason, "Не удалось удалить сервис"));
    }
  };

  const issueToken = async (service: ExternalServiceDto) => {
    const nextName = issueNames[service.id]?.trim() || "";
    if (!nextName) return;

    setIssuingId(service.id);
    setActionError(null);
    try {
      const issued = await issueExternalServiceToken(service.id, nextName);
      if (issued.credential) {
        replaceService({
          ...service,
          tokens: [issued.credential, ...service.tokens],
        });
      } else {
        await reload();
      }
      setIssueNames((current) => ({ ...current, [service.id]: "" }));
      setSecret({
        title: `Новый токен «${nextName}»`,
        token: issued.token,
      });
    } catch (reason) {
      setActionError(errorMessage(reason, "Не удалось выпустить токен"));
    } finally {
      setIssuingId(null);
    }
  };

  const revokeToken = async (
    service: ExternalServiceDto,
    tokenId: number,
    label: string,
  ) => {
    if (!window.confirm(`Отозвать токен «${label}»? Он сразу перестанет работать.`)) {
      return;
    }

    setRevokingId(tokenId);
    setActionError(null);
    try {
      const revoked = await revokeExternalServiceToken(service.id, tokenId);
      replaceService({
        ...service,
        tokens: service.tokens.map((token) =>
          token.id === tokenId ? revoked : token,
        ),
      });
    } catch (reason) {
      setActionError(errorMessage(reason, "Не удалось отозвать токен"));
    } finally {
      setRevokingId(null);
    }
  };

  const toggleExpanded = (serviceId: number) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(serviceId)) next.delete(serviceId);
      else next.add(serviceId);
      return next;
    });
  };

  if (!isAdmin) {
    return (
      <AdminShell compact title="Внешние сервисы" backLink={{ to: "/", label: "К формам" }}>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 font-onest text-sm text-amber-800">
          Доступно только администратору
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      compact
      wide
      title="Внешние сервисы"
      backLink={{ to: "/", label: "К формам" }}
      actions={
        <Button
          variant="secondary"
          className="h-9 px-3 py-0 text-sm"
          disabled={loading}
          onClick={() => void reload()}
        >
          <IconRefresh size={15} />
          Обновить
        </Button>
      }
    >
      {error || actionError ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2" role="alert">
          <p className="font-onest text-sm text-red-700">
            {actionError || error}
          </p>
        </div>
      ) : null}

      <section className="border-b border-slate-200 pb-5">
        <h2 className="font-onest text-sm font-semibold text-slate-900">
          Новый сервис
        </h2>
        <form
          className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(12rem,.7fr)_auto] lg:items-end"
          onSubmit={(event) => void handleCreate(event)}
        >
          <div>
            <FieldLabel htmlFor="external-service-name" size="compact">
              Название
            </FieldLabel>
            <TextInput
              id="external-service-name"
              size="compact"
              value={name}
              placeholder="CRM"
              onChange={setName}
            />
          </div>
          <div>
            <FieldLabel htmlFor="external-service-description" size="compact">
              Описание
            </FieldLabel>
            <TextInput
              id="external-service-description"
              size="compact"
              value={description}
              placeholder="Получение регистраций"
              onChange={setDescription}
            />
          </div>
          <div>
            <FieldLabel htmlFor="external-service-token-name" size="compact">
              Основной токен
            </FieldLabel>
            <TextInput
              id="external-service-token-name"
              size="compact"
              value={tokenName}
              onChange={setTokenName}
            />
          </div>
          <Button
            type="submit"
            className="h-9 px-4 py-0 text-sm"
            disabled={creating || !name.trim() || !tokenName.trim()}
          >
            <IconPlus size={15} />
            {creating ? "Создание…" : "Создать"}
          </Button>
        </form>
      </section>

      <section className="pt-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="font-onest text-sm font-semibold text-slate-900">
            Сервисы
          </h2>
          {!loading ? (
            <span className="font-onest text-xs tabular-nums text-slate-400">
              {services.length}
            </span>
          ) : null}
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {loading ? (
            <div className="px-4 py-10 text-center font-onest text-sm text-slate-500">
              Загрузка…
            </div>
          ) : services.length === 0 ? (
            <div className="px-4 py-10 text-center font-onest text-sm text-slate-500">
              Внешних сервисов пока нет
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {services.map((service) => {
                const isExpanded = expanded.has(service.id);
                const activeTokens = service.tokens.filter(
                  (token) => !token.revokedAt,
                ).length;

                return (
                  <article key={service.id}>
                    <div className="flex flex-wrap items-center gap-3 px-3 py-3 sm:px-4">
                      <button
                        type="button"
                        title={isExpanded ? "Скрыть токены" : "Показать токены"}
                        aria-label={isExpanded ? "Скрыть токены" : "Показать токены"}
                        aria-expanded={isExpanded}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                        onClick={() => toggleExpanded(service.id)}
                      >
                        {isExpanded ? (
                          <IconChevronDown size={16} />
                        ) : (
                          <IconChevronRight size={16} />
                        )}
                      </button>

                      {editingId === service.id ? (
                        <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2">
                          <TextInput
                            size="compact"
                            value={editName}
                            onChange={setEditName}
                            autoFocus
                          />
                          <TextArea
                            size="compact"
                            rows={2}
                            value={editDescription}
                            onChange={setEditDescription}
                            placeholder="Описание"
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => toggleExpanded(service.id)}
                        >
                          <span className="block truncate font-onest text-sm font-semibold text-slate-900">
                            {service.name}
                          </span>
                          <span className="mt-0.5 block truncate font-onest text-xs text-slate-500">
                            {service.description || "Без описания"}
                          </span>
                        </button>
                      )}

                      <div className="ml-auto flex items-center gap-2">
                        <span className="hidden font-onest text-xs text-slate-500 sm:inline">
                          Форм: {service.formsCount}
                        </span>
                        <span className="hidden font-onest text-xs text-slate-500 md:inline">
                          Токенов: {activeTokens}
                        </span>
                        {editingId === service.id ? (
                          <>
                            <Button
                              className="h-8 px-3 py-0 text-xs"
                              disabled={savingId === service.id || !editName.trim()}
                              onClick={() => void saveEdit(service.id)}
                            >
                              <IconCheck size={14} />
                              Сохранить
                            </Button>
                            <button
                              type="button"
                              title="Отменить"
                              aria-label="Отменить"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                              onClick={() => setEditingId(null)}
                            >
                              <IconX size={15} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              title="Редактировать сервис"
                              aria-label="Редактировать сервис"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                              onClick={() => beginEdit(service)}
                            >
                              <IconPencil size={14} />
                            </button>
                            <button
                              type="button"
                              title={
                                service.formsCount > 0
                                  ? "Сначала отвяжите формы"
                                  : "Удалить сервис"
                              }
                              aria-label="Удалить сервис"
                              disabled={service.formsCount > 0}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-500 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-35"
                              onClick={() => void removeService(service)}
                            >
                              <IconTrash size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {isExpanded ? (
                      <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-4 sm:pl-16">
                        <form
                          className="flex max-w-xl flex-col gap-2 sm:flex-row"
                          onSubmit={(event) => {
                            event.preventDefault();
                            void issueToken(service);
                          }}
                        >
                          <TextInput
                            size="compact"
                            value={issueNames[service.id] ?? ""}
                            placeholder="Название нового токена"
                            onChange={(value) =>
                              setIssueNames((current) => ({
                                ...current,
                                [service.id]: value,
                              }))
                            }
                          />
                          <Button
                            type="submit"
                            variant="secondary"
                            className="h-9 shrink-0 px-3 py-0 text-xs"
                            disabled={
                              issuingId === service.id ||
                              !(issueNames[service.id] ?? "").trim()
                            }
                          >
                            <IconKey size={14} />
                            {issuingId === service.id
                              ? "Выпуск…"
                              : "Выпустить токен"}
                          </Button>
                        </form>

                        <div className="mt-4 overflow-x-auto">
                          {service.tokens.length === 0 ? (
                            <p className="font-onest text-sm text-slate-500">
                              Токенов нет
                            </p>
                          ) : (
                            <table className="w-full min-w-[620px] border-collapse text-left">
                              <thead>
                                <tr className="font-onest text-[11px] uppercase text-slate-400">
                                  <th className="pb-2 pr-3 font-semibold">Название</th>
                                  <th className="pb-2 pr-3 font-semibold">Префикс</th>
                                  <th className="pb-2 pr-3 font-semibold">Последнее использование</th>
                                  <th className="pb-2 pr-3 font-semibold">Статус</th>
                                  <th className="w-10 pb-2 font-semibold" />
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200">
                                {service.tokens.map((token) => {
                                  const revoked = Boolean(token.revokedAt);
                                  const label = token.name || token.prefix;
                                  return (
                                    <tr key={token.id} className="font-onest text-xs text-slate-600">
                                      <td className="py-2.5 pr-3 font-medium text-slate-800">
                                        {token.name || "Без названия"}
                                      </td>
                                      <td className="py-2.5 pr-3 font-mono text-[11px]">
                                        {token.prefix}…
                                      </td>
                                      <td className="py-2.5 pr-3">
                                        {formatDateTime(token.lastUsedAt)}
                                      </td>
                                      <td className="py-2.5 pr-3">
                                        <span
                                          className={
                                            revoked
                                              ? "text-slate-400"
                                              : "font-medium text-emerald-700"
                                          }
                                        >
                                          {revoked ? "Отозван" : "Активен"}
                                        </span>
                                      </td>
                                      <td className="py-1 text-right">
                                        {!revoked ? (
                                          <button
                                            type="button"
                                            title="Отозвать токен"
                                            aria-label="Отозвать токен"
                                            disabled={revokingId === token.id}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                                            onClick={() =>
                                              void revokeToken(
                                                service,
                                                token.id,
                                                label,
                                              )
                                            }
                                          >
                                            <IconTrash size={14} />
                                          </button>
                                        ) : null}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {secret ? (
        <SecretDialog
          title={secret.title}
          token={secret.token}
          onClose={() => setSecret(null)}
        />
      ) : null}
    </AdminShell>
  );
}
